import { useState } from "react"
import {
  Dialog,
  Checkbox,
  DialogBody,
  DialogFooter,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core"
import React from "react"
import dayjs from "dayjs"
import CustomButton from "components/Atoms/CustomButton"
import { Ticket } from "@phosphor-icons/react"
import { useGatepassSelectorQuery } from "generated/graphql"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "store"
import { GatePassStateType, updatePallet } from "store/production.reducer"

interface GatePassSelectorProps {
  index: number
  productionData: any
}

const GatePassSelector: React.FC<GatePassSelectorProps> = ({
  index,
  productionData,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelected, setTempSelected] = useState<any[]>([])
  const [inputWeights, setInputWeights] = useState<Record<string, number>>({})
  const [remainingWeightModal, setRemainingWeightModal] = useState<{
    isOpen: boolean
    gatePasses: Array<{
      id: string
      name: string
      remainingWeight: number
      rejectionAmount: number
    }>
  }>({
    isOpen: false,
    gatePasses: [],
  })

  const handleWeightInput = (id: string, value: number, max: number) => {
    const validWeight = Math.min(value, max)
    setInputWeights(prev => ({ ...prev, [id]: validWeight }))
  }

  const dispatch = useDispatch()
  const production = useSelector(
    (state: ApplicationState) => state.production.production
  )
  const pallets = production?.pallets ?? []
  const pallet = pallets[index]

  // Editable until the production is sent for approval / verified
  const isLocked = (() => {
    if (productionData?.sendForApproval) return true
    try {
      const meta = productionData?.metadata
        ? JSON.parse(productionData.metadata)
        : null
      return meta?.status === "verify"
    } catch {
      return false
    }
  })()

  const { data } = useGatepassSelectorQuery({
    variables: {
      productionId: productionData?.id,
      productionLineId: productionData?.productionLine?.id,
    },
    fetchPolicy: "network-only",
    skip: !isOpen,
  })

  const allGatePasses =
    data?.gatepassSelector?.map(item => {
      const schedule = item.inwardAllocation?.inward?.booking?.schedule
      const rejectedWeight = item?.rejectedWeight ?? 0
      const availableWeight =
        (item?.allotedWeight ?? 0) - (item?.consumedWeight ?? 0)

      const soVarieties = productionData?.shipmentPlan?.varieties ?? []
      const varietyName = schedule?.variety?.name ?? ""
      const parentVarietyName = schedule?.variety?.parentVariety?.name ?? ""

      const isVarietyInSO = soVarieties.some(v => v?.name === varietyName)

      const finalVariety = isVarietyInSO
        ? varietyName
        : parentVarietyName || varietyName
      const finalParentVariety = isVarietyInSO ? "" : varietyName

      return {
        id: item?.id ?? "",
        gatePass: schedule?.gatePass ?? "",
        farmer: `${schedule?.plot?.farmer?.fullName ?? ""}${
          schedule?.plotCommodity?.registrationCode
            ? ` [${schedule.plotCommodity.registrationCode}]`
            : ""
        }`,
        location: schedule?.plot?.farmer?.address?.address ?? "",
        variety: finalVariety,
        parentVariety: finalParentVariety,
        weight: availableWeight > 0 ? availableWeight : 0,
        rejectedWeight,
        rejectedQuantity: item?.rejectedQuantity ?? 0,
      }
    }) ?? []

  const handleOpenDialog = () => {
    setInputWeights({})
    const currentPalletSelected = pallet?.gatePasses ?? []
    setTempSelected(currentPalletSelected)
    setIsOpen(true)
  }

  const toggleSelect = (gp: any) => {
    setTempSelected(prev => {
      const alreadySelected = prev.find(p => p.id === gp.id)

      if (alreadySelected) {
        setInputWeights(prevWeights => {
          const newWeights = { ...prevWeights }
          delete newWeights[gp.id]
          return newWeights
        })
        return prev.filter(p => p.id !== gp.id)
      } else {
        setInputWeights(prevWeights => ({
          ...prevWeights,
          [gp.id]: +(gp.weight?.toFixed(2) ?? 0),
        }))
        return [...prev, gp]
      }
    })
  }

  const metadata = productionData?.shipmentPlan?.metadata
    ? JSON.parse(productionData?.shipmentPlan?.metadata)
    : {}
  const isEuro = metadata?.is_euro ?? false
  const euroBoxes = metadata?.euro_pallet_box_count ?? 0
  const regularBoxes = metadata?.regular_pallet_box_count ?? 0
  const skuFilled = productionData?.shipmentPlan?.skuFilled ?? 0
  const skuCapacity = productionData?.shipmentPlan?.skuType?.capacity ?? 1

  let requiredBoxes = 0
  if (skuFilled > 0) {
    requiredBoxes = isEuro
      ? euroBoxes + (skuFilled - 1) * regularBoxes
      : skuFilled * regularBoxes
  }
  const requiredWeight = requiredBoxes * skuCapacity

  const mixConfirmSelection = () => {
    if (!tempSelected.length) return

    const varieties = productionData?.shipmentPlan?.varieties ?? []
    if (!varieties.length) return

    const meta = productionData?.shipmentPlan?.metadata
      ? JSON.parse(productionData?.shipmentPlan?.metadata)
      : {}
    const updatedPallets = [...pallets]

    // Build queues: GatePasses grouped by variety
    const varietyQueues: Record<string, any[]> = {}
    varieties.forEach(v => {
      const gps = tempSelected.filter(gp => gp.variety === v.name)
      varietyQueues[v.id] = gps.map(gp => {
        const editedWeight = inputWeights[gp.id] ?? gp.weight ?? 0
        // Subtract the weight already assigned across ALL pallets so we don't
        // re-add weight that was committed in a previous confirmation round.
        const alreadyAssignedTotal = pallets
          .flatMap(p => p.gatePasses ?? [])
          .filter(x => x.id === gp.id)
          .reduce((sum, x) => sum + (x.weight ?? 0), 0)
        const remaining = Math.max(0, editedWeight - alreadyAssignedTotal)
        return {
          ...gp,
          remaining,
        }
      })
    })

    for (let pIndex = 0; pIndex < updatedPallets.length; pIndex++) {
      const pallet = updatedPallets[pIndex]
      const requiredWeightForPallet = pallet.isEuro
        ? (meta.euro_pallet_box_count ?? 0) *
          (productionData?.shipmentPlan?.skuType?.capacity ?? 1)
        : (meta.regular_pallet_box_count ?? 0) *
          (productionData?.shipmentPlan?.skuType?.capacity ?? 1)

      // Current filled weight
      let alreadyFilled =
        pallet.gatePasses?.reduce((sum, gp) => sum + gp.weight, 0) ?? 0

      // Skip pallet if already full
      if (alreadyFilled >= requiredWeightForPallet) continue

      let allocatedWeight = alreadyFilled
      let gpAssignments: GatePassStateType[] = [...(pallet.gatePasses ?? [])]

      // Divide required weight equally across varieties
      const perVariety = requiredWeightForPallet / varieties.length

      for (const v of varieties) {
        // Calculate how much of this variety is already in the pallet
        const currentVarietyWeight = gpAssignments
          .filter(gp => {
            const gpData = allGatePasses.find(x => x.id === gp.id)
            return gpData?.variety === v.name
          })
          .reduce((sum, gp) => sum + (gp.weight ?? 0), 0)

        // Calculate remaining quota for this variety
        let quota = perVariety - currentVarietyWeight

        // Adjust quota so pallet never overfills
        quota = Math.min(quota, requiredWeightForPallet - allocatedWeight)
        if (quota <= 0) continue // variety quota already filled or pallet full

        const queue = varietyQueues[v.id] ?? []

        while (quota > 0 && queue.length > 0) {
          const gp = queue[0]
          const assignWeight = Math.min(gp.remaining, quota)
          const assignBoxes = +(
            assignWeight /
            (productionData?.shipmentPlan?.skuType?.capacity ?? 1)
          ).toFixed(2)

          if (assignWeight > 0) {
            const existingIdx = gpAssignments.findIndex(x => x.id === gp.id)
            if (existingIdx >= 0) {
              gpAssignments[existingIdx].weight = +(
                gpAssignments[existingIdx].weight + assignWeight
              ).toFixed(2)
              gpAssignments[existingIdx].boxes = +(
                gpAssignments[existingIdx].boxes + assignBoxes
              ).toFixed(2)
            } else {
              gpAssignments.push({
                id: gp.id,
                gatePass: gp.gatePass,
                weight: +assignWeight.toFixed(2),
                boxes: +assignBoxes.toFixed(2),
                farmerName: gp.farmer,
                variety: gp.variety,
              })
            }

            allocatedWeight += assignWeight
            gp.remaining -= assignWeight
            quota -= assignWeight
          }

          if (gp.remaining <= 0) {
            queue.shift()
          }

          // Stop assigning if pallet reaches capacity
          if (allocatedWeight >= requiredWeightForPallet) break
        }

        // Update the queue back to ensure changes persist
        varietyQueues[v.id] = queue

        if (allocatedWeight >= requiredWeightForPallet) break
      }

      const totalWeight = +gpAssignments
        .reduce((sum, gp) => sum + gp.weight, 0)
        .toFixed(2)
      const capacity = productionData?.shipmentPlan?.skuType?.capacity ?? 1
      const updatedPallet = {
        ...pallet,
        gatePasses: gpAssignments,
        // Derive boxCount from total weight / capacity so individual rounding
        // errors don't accumulate (e.g. 124.9966... instead of 125).
        boxCount: Math.round(totalWeight / capacity),
        weightCount: totalWeight,
      }

      updatedPallets[pIndex] = updatedPallet
    }

    // Dispatch all pallet updates
    updatedPallets.forEach((pallet, index) => {
      dispatch(updatePallet(pallet, index))
    })

    // Check for remaining weight in variety queues after initial distribution
    const remainingGatePasses: Array<{
      id: string
      name: string
      remainingWeight: number
      rejectionAmount: number
    }> = []

    for (const varietyId in varietyQueues) {
      const queue = varietyQueues[varietyId]
      for (const gp of queue) {
        if (gp.remaining > 0.01) {
          // Small threshold to avoid floating point issues
          remainingGatePasses.push({
            id: gp.id,
            name: gp.gatePass,
            remainingWeight: gp.remaining,
            rejectionAmount: 0,
          })
        }
      }
    }

    // If there are any gatepasses with remaining weight, show modal
    if (remainingGatePasses.length > 0) {
      setRemainingWeightModal({
        isOpen: true,
        gatePasses: remainingGatePasses,
      })
      return // Don't close the dialog yet
    }

    const skuCapacity = productionData?.shipmentPlan?.skuType?.capacity ?? 1

    // Track remaining weights from additional weight distribution
    const additionalRemainingGatePasses: Array<{
      id: string
      name: string
      remainingWeight: number
      rejectionAmount: number
    }> = []

    // Handle additional weight distribution (only for already-assigned gatepasses)
    for (const gpId in inputWeights) {
      const newTotal = inputWeights[gpId]
      const totalAssignedBefore = pallets
        .flatMap(p => p.gatePasses ?? [])
        .filter(x => x.id === gpId)
        .reduce((sum, x) => sum + (x.weight ?? 0) + (x?.rejectedWeight ?? 0), 0)

      // Skip if this gatepass was not previously assigned (it was handled by initial distribution)
      if (totalAssignedBefore === 0) continue

      const additionalWeight = newTotal - totalAssignedBefore
      if (additionalWeight <= 0) continue

      const gp = allGatePasses.find(x => x.id === gpId)
      if (!gp) continue

      let remainingAdditional = additionalWeight

      // Distribute additional weight sequentially across pallets
      for (let pIndex = 0; pIndex < pallets.length; pIndex++) {
        if (remainingAdditional <= 0) break

        const pallet = pallets[pIndex]

        const palletCapacity =
          (pallet.isEuro
            ? meta.euro_pallet_box_count
            : meta.regular_pallet_box_count) * skuCapacity

        // Calculate variety quota for this pallet
        const perVariety = palletCapacity / varieties.length

        // Find current ACCEPTED weight in pallet (NOT including rejections)
        // Rejections don't fill the pallet - they create a need for more weight
        const currentTotalWeight =
          pallet.gatePasses?.reduce((sum, g) => sum + (g.weight ?? 0), 0) ?? 0

        // Find current ACCEPTED weight of this variety in pallet (NOT including rejections)
        const currentVarietyWeight =
          pallet.gatePasses
            ?.filter(g => {
              const gpData = allGatePasses.find(x => x.id === g.id)
              return gpData?.variety === gp.variety
            })
            .reduce((sum, g) => sum + (g.weight ?? 0), 0) ?? 0

        // Calculate how much more this variety can take in this pallet
        const varietyQuotaRemaining = Math.max(
          0,
          perVariety - currentVarietyWeight
        )

        // Also check total pallet capacity remaining
        const palletCapacityRemaining = Math.max(
          0,
          palletCapacity - currentTotalWeight
        )

        // Can't add more if variety quota is full OR pallet is full
        if (varietyQuotaRemaining <= 0 || palletCapacityRemaining <= 0) continue

        // Check if this pallet already has this gatepass
        const existingGP = pallet.gatePasses?.find(g => g.id === gp.id)

        // Assign weight: minimum of (remaining additional, variety quota remaining, pallet capacity remaining)
        const assignWeight = Math.min(
          remainingAdditional,
          varietyQuotaRemaining,
          palletCapacityRemaining
        )

        if (assignWeight <= 0) continue

        let updatedGatePasses: GatePassStateType[]

        if (existingGP) {
          // Update existing gatepass entry
          updatedGatePasses = pallet.gatePasses!.map(g =>
            g.id === gp.id
              ? {
                  ...g,
                  weight: +(g.weight + assignWeight).toFixed(2),
                  boxes: +((g.boxes ?? 0) + assignWeight / skuCapacity).toFixed(
                    2
                  ),
                  rejectedWeight: g.rejectedWeight ?? 0,
                }
              : g
          )
        } else {
          // Add new gatepass entry to this pallet
          updatedGatePasses = [
            ...(pallet.gatePasses ?? []),
            {
              id: gp.id,
              gatePass: gp.gatePass,
              weight: +assignWeight.toFixed(2),
              boxes: +(assignWeight / skuCapacity).toFixed(2),
              farmerName: gp.farmer,
              variety: gp.variety,
              rejectedWeight: 0,
            },
          ]
        }

        const totalWeight = updatedGatePasses.reduce(
          (sum, g) => sum + (g.weight ?? 0) + (g.rejectedWeight ?? 0),
          0
        )
        const totalBoxes = updatedGatePasses.reduce(
          (sum, g) => sum + (g.boxes ?? 0),
          0
        )

        const updatedPallet = {
          ...pallet,
          gatePasses: updatedGatePasses,
          weightCount: +totalWeight.toFixed(2),
          boxCount: +totalBoxes.toFixed(2),
        }

        dispatch(updatePallet(updatedPallet, pIndex))
        remainingAdditional -= assignWeight
      }

      // Collect remaining weight if any
      if (remainingAdditional > 0.01) {
        additionalRemainingGatePasses.push({
          id: gpId,
          name: gp.gatePass,
          remainingWeight: remainingAdditional,
          rejectionAmount: 0,
        })
      }
    }

    // If there are any gatepasses with remaining weight from additional distribution, show modal
    if (additionalRemainingGatePasses.length > 0) {
      setRemainingWeightModal({
        isOpen: true,
        gatePasses: additionalRemainingGatePasses,
      })
      return // Don't close the dialog yet
    }

    setTempSelected([])
    setIsOpen(false)
  }
  const hasWeightChanges = Object.keys(inputWeights).length > 0

  const validateGatePassSelection = () => {
    const varieties = productionData?.shipmentPlan?.varieties ?? []
    if (!varieties.length) return true
    return varieties.every(v => tempSelected.some(gp => gp.variety === v.name))
  }

  const isGatePassSelectionValid = validateGatePassSelection()

  const valueForAddtionWeight = gp => {
    const newTotal = inputWeights[gp.id]
    const totalAssignedBefore = pallets
      .flatMap(p => p.gatePasses ?? [])
      .filter(x => x.id === gp.id)
      .reduce((sum, x) => sum + (x.weight ?? 0) + (x?.rejectedWeight ?? 0), 0)

    // Only distribute the additional (accepted) weight
    const additionalWeight = newTotal - totalAssignedBefore
    return additionalWeight?.toFixed(2)
  }

  const handleRemainingWeightDecision = (addToRejection: boolean) => {
    const { gatePasses } = remainingWeightModal

    if (addToRejection) {
      // Track which pallets have been updated
      const updatedPalletsMap = new Map<number, any>()

      // Process each gatepass with rejection amount
      gatePasses.forEach(gp => {
        if (gp.rejectionAmount > 0) {
          // Find the last pallet that has this gatepass
          const lastPalletIndex = pallets.length - 1
          for (let i = lastPalletIndex; i >= 0; i--) {
            // Get the pallet from the map if already updated, otherwise from pallets array
            const pallet = updatedPalletsMap.get(i) || pallets[i]
            const existingGP = pallet.gatePasses?.find(g => g.id === gp.id)

            if (existingGP) {
              // Add the rejection amount to this pallet
              const updatedGatePasses = pallet.gatePasses!.map(g =>
                g.id === gp.id
                  ? {
                      ...g,
                      rejectedWeight:
                        +(g.rejectedWeight ?? 0) + gp.rejectionAmount,
                    }
                  : g
              )

              const totalWeight = updatedGatePasses.reduce(
                (sum, g) => sum + (g.weight ?? 0) + (g.rejectedWeight ?? 0),
                0
              )

              const updatedPallet = {
                ...pallet,
                gatePasses: updatedGatePasses,
                weightCount: +totalWeight.toFixed(2),
              }

              // Store the updated pallet in the map
              updatedPalletsMap.set(i, updatedPallet)
              break
            }
          }
        }
      })

      // Dispatch all updated pallets
      updatedPalletsMap.forEach((pallet, index) => {
        dispatch(updatePallet(pallet, index))
      })
    }

    // Reset modal and close dialogs
    setRemainingWeightModal({
      isOpen: false,
      gatePasses: [],
    })
    setTempSelected([])
    setIsOpen(false)
  }

  return (
    <>
      <FormGroup
        label={
          <>
            Gate Pass <span className="text-red">*</span>
          </>
        }
        helperText={
          <span className="text-[#1F6D99] text-xs">
            Assigned:{" "}
            {(() => {
              const activeGPs =
                pallet?.gatePasses?.filter(v => (v?.weight ?? 0) > 0) ?? []
              return activeGPs.length > 0
                ? activeGPs
                    .map(
                      v =>
                        `${v?.gatePass} (${v?.weight?.toFixed(
                          2
                        )} kg / ${v?.boxes?.toFixed(2)} boxes)`
                    )
                    .join(", ")
                : "Select Gate Pass"
            })()}
          </span>
        }
      >
        <div
          onClick={handleOpenDialog}
          style={{ border: "1px solid" }}
          className="flex items-center justify-between cursor-pointer px-3 py-[5.5px] rounded border bg-white border-slate-300"
        >
          <span>
            {(() => {
              const activeGPs =
                pallet?.gatePasses?.filter(v => (v?.weight ?? 0) > 0) ?? []
              return activeGPs.length > 0
                ? activeGPs.map(v => `${v.gatePass}`).join(", ")
                : "Select Gate Pass"
            })()}
          </span>
          <span className="bp4-icon bp4-icon-caret-down" />
        </div>
      </FormGroup>

      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <Ticket size={24} /> Select Gate Pass
          </div>
        }
      >
        <DialogBody className="max-h-[70vh] overflow-y-auto bg-white">
          <div className="bg-blue-50 text-sm p-3 rounded border border-slate-200 my-2">
            <div>
              <b>Packing Format:</b>{" "}
              {productionData?.shipmentPlan?.skuType?.name ?? "--"} |{" "}
              <b>Required Weight:</b> {requiredWeight} kg
            </div>
          </div>

          <div className="space-y-3">
            {allGatePasses.map(gp => {
              const alreadyAssigned = pallets.some(p =>
                (p.gatePasses ?? []).some(g => g.id === gp.id)
              )
              const disabled = gp.weight <= 0 || isLocked
              const assignedWeight = pallets
                .flatMap(p => p.gatePasses ?? [])
                .filter(x => x.id === gp.id)
                .reduce(
                  (sum, x) => sum + (x.weight ?? 0) + (x?.rejectedWeight ?? 0),
                  0
                )

              // Check if this gate pass has been saved (update mode)
              const hasResponseId = pallets
                .flatMap(p => p.gatePasses ?? [])
                .some(x => x.id === gp.id && x.responseId)

              // In update mode: show additional field if there's any available weight
              // In create mode: show only if available weight > assigned weight
              const isValid = hasResponseId
                ? gp.weight > 0 // Update: any available weight
                : gp.weight - assignedWeight > 0 // Create: available > assigned

              const isSelected = tempSelected.some(p => p.id === gp.id)

              return (
                <div
                  key={gp.id}
                  style={{ border: "1px solid" }}
                  className={`flex flex-col gap-2 p-5 rounded-lg border ${
                    disabled
                      ? "border-gray-300 bg-gray-100 opacity-70"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex items-start justify-between w-full">
                    <Checkbox
                      disabled={disabled}
                      checked={isSelected}
                      onChange={() => toggleSelect(gp)}
                    />

                    <div className="space-y-2 w-full">
                      <div className="flex gap-2 justify-between items-center">
                        <b>{gp.gatePass}</b>
                        {(() => {
                          const assignedHere = (pallet?.gatePasses ?? []).some(
                            g => g.id === gp.id
                          )
                          const lots = assignedHere
                            ? (pallet?.lotCode ?? []).filter(
                                l => l.gatePass === gp.gatePass
                              )
                            : []
                          if (lots.length === 0) return null
                          return (
                            <div className="text-xs">
                              {lots
                                .map(l => {
                                  const created = l.productionCreatedDate
                                    ? dayjs(l.productionCreatedDate)
                                    : null
                                  const time = created?.isValid()
                                    ? created.format("DD MMM 'YY")
                                    : l.date
                                  return `${l.lotCode || "--"} - ${time}`
                                })
                                .join(", ")}
                            </div>
                          )
                        })()}
                      </div>

                      <div>
                        <div>{gp.farmer}</div>
                        <div className="text-gray-500 text-xs">
                          {gp.location}
                        </div>
                      </div>
                      <div className="flex gap-2 justify-between items-center">
                        <div className="text-gray-700 text-sm">
                          {gp.variety}{" "}
                          <span className="text-gray-500 text-xs">
                            {gp.parentVariety ? `(${gp.parentVariety})` : ""}
                          </span>
                        </div>
                        <div className="text-orange-600 font-bold">
                          {gp.weight.toLocaleString()} kg
                        </div>
                      </div>

                      {isSelected && (
                        <InputGroup
                          disabled={disabled}
                          value={
                            inputWeights[gp.id] !== undefined
                              ? String(inputWeights[gp.id])
                              : String(
                                  pallets
                                    .flatMap(p => p.gatePasses ?? [])
                                    .filter(x => x.id === gp.id)
                                    .reduce(
                                      (sum, x) =>
                                        sum +
                                        (x.weight ?? 0) +
                                        (x?.rejectedWeight ?? 0),
                                      0
                                    ) || gp.weight
                                )
                          }
                          onChange={e => {
                            const newWeight = parseFloat(e.target.value)
                            if (!isNaN(newWeight)) {
                              // For new selections, max is just the available weight
                              handleWeightInput(gp.id, newWeight, gp.weight)
                            }
                          }}
                          type="number"
                          placeholder={`Enter weight (max ${gp.weight?.toFixed(
                            2
                          )} kg)`}
                          min={0}
                          max={gp.weight?.toFixed(2)}
                        />
                      )}
                      {alreadyAssigned &&
                        isValid &&
                        (() => {
                          // Check if this is an update case (has responseId) or create case (no responseId)
                          const hasResponseId = pallets
                            .flatMap(p => p.gatePasses ?? [])
                            .some(x => x.id === gp.id && x.responseId)

                          // Calculate total already assigned (including rejections)
                          const totalAssignedBefore = pallets
                            .flatMap(p => p.gatePasses ?? [])
                            .filter(x => x.id === gp.id)
                            .reduce(
                              (sum, x) =>
                                sum +
                                parseFloat((x.weight ?? 0).toFixed(2)) +
                                parseFloat((x.rejectedWeight ?? 0).toFixed(2)),
                              0
                            )

                          // In update case: gp.weight is already the available weight (backend accounts for consumed)
                          // In create case: gp.weight is total available, need to subtract what's already assigned
                          const maxAdditionalWeight = hasResponseId
                            ? parseFloat(gp.weight.toFixed(2)) // Update: use available weight directly
                            : Math.max(
                                0,
                                parseFloat(
                                  (gp.weight - assignedWeight).toFixed(2)
                                )
                              ) // Create: available - assigned

                          return (
                            <InputGroup
                              type="number"
                              disabled={disabled}
                              placeholder={`Enter additional weight (max ${maxAdditionalWeight.toFixed(
                                2
                              )} kg)`}
                              min={0}
                              step="0.01"
                              value={
                                inputWeights[gp.id] !== undefined
                                  ? String(
                                      parseFloat(
                                        Math.max(
                                          0,
                                          inputWeights[gp.id] -
                                            totalAssignedBefore
                                        ).toFixed(2)
                                      )
                                    )
                                  : ""
                              }
                              max={maxAdditionalWeight}
                              onChange={e => {
                                let additionalWeight = parseFloat(
                                  e.target.value
                                )
                                if (
                                  isNaN(additionalWeight) ||
                                  additionalWeight < 0
                                ) {
                                  // Allow 0 to clear the field
                                  if (
                                    additionalWeight === 0 ||
                                    e.target.value === ""
                                  ) {
                                    setInputWeights(prev => {
                                      const newWeights = { ...prev }
                                      delete newWeights[gp.id]
                                      return newWeights
                                    })
                                  }
                                  return
                                }

                                // Limit additional weight to max additional weight
                                if (additionalWeight > maxAdditionalWeight) {
                                  additionalWeight = parseFloat(
                                    maxAdditionalWeight.toFixed(2)
                                  )
                                }

                                // The new target total user wants to reach
                                // Total = what's already assigned + the additional weight being added
                                const newTotal = parseFloat(
                                  (
                                    totalAssignedBefore + additionalWeight
                                  ).toFixed(2)
                                )

                                // Max assignable depends on whether it's create or update case
                                const maxAssignable = hasResponseId
                                  ? parseFloat(
                                      (
                                        totalAssignedBefore +
                                        maxAdditionalWeight
                                      ).toFixed(2)
                                    ) // Update: assigned + available
                                  : parseFloat(gp.weight.toFixed(2)) // Create: total available weight

                                // Limit total to max assignable
                                handleWeightInput(
                                  gp.id,
                                  newTotal,
                                  maxAssignable
                                )
                              }}
                            />
                          )
                        })()}

                      {(() => {
                        // Find same gatepass in current pallet assignments
                        const assignedGP = pallets
                          .flatMap(p => p.gatePasses ?? [])
                          .find(x => x.id === gp.id)

                        const rejected = assignedGP?.rejectedWeight ?? 0

                        return rejected > 0 ? (
                          <>
                            Rejected Weight:{" "}
                            <span className="text-red-600 font-bold">
                              {rejected.toLocaleString()} kg
                            </span>
                          </>
                        ) : null
                      })()}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex flex-col w-full gap-2">
            {/* Error message */}
            {!isGatePassSelectionValid && (
              <div className="text-red-600 text-xs">
                Please select at least one Gate Pass for each variety.
              </div>
            )}

            <div className="flex justify-between items-center w-full">
              <CustomButton onClick={() => setIsOpen(false)} round>
                Cancel
              </CustomButton>
              <CustomButton
                intent="success"
                round
                disabled={!isGatePassSelectionValid && !hasWeightChanges}
                onClick={mixConfirmSelection}
              >
                Confirm
              </CustomButton>
            </div>
          </div>
        </DialogFooter>
      </Dialog>

      {/* Remaining Weight Modal */}
      <Dialog
        isOpen={remainingWeightModal.isOpen}
        onClose={() => handleRemainingWeightDecision(false)}
        title="Remaining Weight Detected"
        style={{ width: "600px" }}
      >
        <DialogBody className="bg-white max-h-[70vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <p className="text-sm font-semibold">
                {remainingWeightModal.gatePasses.length} Gate Pass
                {remainingWeightModal.gatePasses.length > 1 ? "es" : ""} with
                remaining weight
              </p>
            </div>

            <div className="text-sm text-gray-700">
              <p>
                The following gate passes have remaining weight that couldn't be
                distributed to the pallets. You can add any amount to rejection
                for each gate pass.
              </p>
            </div>

            <div className="space-y-3">
              {remainingWeightModal.gatePasses.map((gp, index) => (
                <div
                  key={gp.id}
                  className="border border-slate-200 rounded p-4 space-y-3"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{gp.name}</p>
                      <p className="text-sm text-gray-600">
                        Remaining:{" "}
                        <span className="text-orange-600 font-bold">
                          {gp.remainingWeight.toFixed(2)} kg
                        </span>
                      </p>
                    </div>
                  </div>

                  <FormGroup
                    label="Rejection Amount (kg)"
                    helperText={`Maximum: ${gp.remainingWeight.toFixed(2)} kg`}
                  >
                    <InputGroup
                      type="number"
                      placeholder="Enter rejection amount"
                      min={0}
                      max={gp.remainingWeight}
                      value={String(gp.rejectionAmount)}
                      onChange={e => {
                        const value = parseFloat(e.target.value)
                        if (
                          !isNaN(value) &&
                          value >= 0 &&
                          value <= gp.remainingWeight
                        ) {
                          setRemainingWeightModal(prev => ({
                            ...prev,
                            gatePasses: prev.gatePasses.map((item, i) =>
                              i === index
                                ? { ...item, rejectionAmount: value }
                                : item
                            ),
                          }))
                        }
                      }}
                    />
                  </FormGroup>
                </div>
              ))}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex justify-between items-center w-full">
            <CustomButton
              onClick={() => handleRemainingWeightDecision(false)}
              round
            >
              Keep Available
            </CustomButton>
            <CustomButton
              intent="danger"
              round
              disabled={
                !remainingWeightModal.gatePasses.some(
                  gp => gp.rejectionAmount > 0
                )
              }
              onClick={() => handleRemainingWeightDecision(true)}
            >
              Add to Rejection
            </CustomButton>
          </div>
        </DialogFooter>
      </Dialog>
    </>
  )
}

export default GatePassSelector
